/**
 * Client ของ `POST /v1/me/password` — contract ที่ freeze ไว้ใน
 * `docs/API_CONTRACT.md` (D-048/D-050/D-051) และ implement แล้วที่
 * `apps/api/src/cap_erp_api/modules/rbac/auth_router.py`
 *
 * หลักการเดียวกับ `client.ts`: ฟังก์ชันนี้ **ไม่ throw** ทุกคำตอบที่ contract ระบุไว้
 * กลายเป็นสถานะที่ผู้เรียกต้องแจกแจงเอง
 *
 * สามข้อที่ต่างจาก client อื่นในแอปนี้และตั้งใจให้ต่าง:
 *
 * 1. **ไม่ผ่าน `authorizedFetch`** ทั้งที่เป็น endpoint ที่ต้อง login — เพราะตัวนั้น
 *    ดัก `401` แล้วสั่ง `endSession('UNAUTHORIZED')` ซึ่งถูกต้องสำหรับ session ที่ถูก
 *    revoke แต่ **ผิด** สำหรับที่นี่: `401` ของ endpoint นี้แปลว่า "รหัสผ่านปัจจุบัน
 *    ไม่ถูกต้อง" ได้ด้วย (`docs/API_CONTRACT.md`) การเตะผู้ใช้ออกจากระบบเพราะพิมพ์
 *    รหัสเดิมผิดหนึ่งครั้ง คือการทำให้ความผิดพลาดที่แก้ได้กลายเป็นการเสียเซสชัน
 * 2. **token มาจากผู้เรียก ไม่ได้มาจาก session store** — เส้นทางกู้คืนของ D-050 ยื่น
 *    `password_change_token` ที่ **ไม่ใช่เซสชัน** และต้องไม่เคยถูกเขียนลง store/storage
 *    ที่นี่จึงรับ token เป็นพารามิเตอร์เสมอ ไม่มีค่า default ที่หยิบจากที่อื่นมาเงียบ ๆ
 * 3. **`204` ไม่มี body ให้ parse** — ความสำเร็จอ่านจาก status เท่านั้น
 *
 * ⚠️ ห้ามใส่รหัสผ่าน (ทั้งเดิมและใหม่) ลงในค่าที่คืนกลับ ลง log หรือลง error ใด ๆ
 * (`docs/SECURITY.md`)
 */

import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/** เส้นทางจริงที่ client เรียก — ตาราง endpoint ใน contract ตัด base prefix `/v1` ออก */
export const CHANGE_PASSWORD_PATH = '/v1/me/password';

/**
 * `details.rule` ของ `422` — ค่าที่เซิร์ฟเวอร์ส่งจริง (`modules/rbac/passwords.py`)
 *
 * แยกกันสองค่าเพราะผู้ใช้ต้องแก้คนละอย่าง: ตัวหนึ่งคือ "ยังสั้นไป" อีกตัวคือ
 * "ยังไม่มีอักขระพิเศษ" — การรวมเป็นข้อความเดียวจะทำให้คนที่ตั้งรหัสยาวพอแล้วยังคง
 * เดาต่อไปว่าอะไรไม่ผ่าน
 */
export const PASSWORD_TOO_SHORT_RULE = 'PASSWORD_TOO_SHORT';
export const PASSWORD_NEEDS_SPECIAL_CHARACTER_RULE = 'PASSWORD_NEEDS_SPECIAL_CHARACTER';
/** ตั้งรหัสใหม่เป็นรหัสเดิมไม่ได้ (รวมรหัสชั่วคราวที่ผู้ดูแลเห็น) */
export const PASSWORD_SAME_AS_CURRENT_RULE = 'PASSWORD_SAME_AS_CURRENT';

export interface PasswordChange {
  readonly currentPassword: string;
  readonly newPassword: string;
}

export type PasswordChangeResult =
  /** `204 No Content` — เปลี่ยนสำเร็จ ไม่มี body และไม่มี token ใหม่ */
  | { readonly status: 'changed' }
  /**
   * `401` — รหัสผ่านปัจจุบันผิด **หรือ** token ที่ยื่นไปใช้ไม่ได้แล้ว
   *
   * เซิร์ฟเวอร์ตอบเหมือนกันทั้งสองกรณี และที่นี่ก็ไม่แยกเช่นกัน
   * `docs/API_CONTRACT.md` ระบุชัดว่า **ไม่นับ failed attempt และไม่ทำให้ถูก
   * lockout** ผู้เรียกจึงต้องไม่บอกผู้ใช้เรื่องการถูกล็อก และต้องไม่นับครั้งเอง
   */
  | { readonly status: 'rejected' }
  /** `403` — เช่น service account ที่ไม่มีรหัสผ่านของตัวเองให้เปลี่ยน */
  | { readonly status: 'forbidden' }
  /** `400` — body ไม่ตรงรูปแบบที่เซิร์ฟเวอร์รับ */
  | { readonly status: 'bad-request' }
  /**
   * `422` — รหัสใหม่ผิดนโยบาย D-047 พร้อม `details.rule`
   *
   * `rule` เป็น `null` ได้เมื่อ envelope ไม่มี rule มาด้วย — ไม่เดาแทนเซิร์ฟเวอร์ว่า
   * ข้อไหนไม่ผ่าน เพราะการเดาผิดจะพาผู้ใช้ไปแก้ผิดจุด
   */
  | { readonly status: 'policy-violation'; readonly rule: string | null }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string };

export interface PasswordChangeOptions {
  /**
   * token ที่ใช้ยืนยันตัวตนของคำขอนี้ — **บังคับ** และมีได้สองแบบ:
   *
   * - session token ปกติ (เส้นทางเปลี่ยนรหัสด้วยความสมัครใจ; เซิร์ฟเวอร์เก็บ session
   *   นี้ไว้ให้)
   * - `password_change_token` ของ D-050 (เส้นทางกู้คืน; เซิร์ฟเวอร์ฆ่าทุก session
   *   รวมทั้งตัวนี้เมื่อสำเร็จ)
   *
   * client ไม่จำเป็นต้องรู้ว่าตัวไหน — เซิร์ฟเวอร์อ่าน `must_change_password` เอง
   */
  readonly token: string;
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ */
  readonly fetchImpl?: typeof fetch;
}

/**
 * body ที่ส่งออกไป — **เฉพาะสองคีย์นี้เท่านั้น**
 *
 * เซิร์ฟเวอร์ประกาศ `extra = "forbid"` ไว้ คีย์ส่วนเกินจึงไม่ได้แค่ถูกเมิน แต่ทำให้
 * ทั้งคำขอกลายเป็น `400` — และเช่นเดียวกับ `serializeLoginBody` ตรงนี้ประกอบทีละ
 * ฟิลด์ ไม่ใช่ spread เพื่อให้ของที่ติดมากับ state ของฟอร์มไม่มีทางหลุดออกไป
 */
export function serializePasswordChangeBody(change: PasswordChange): Record<string, unknown> {
  return {
    current_password: change.currentPassword,
    new_password: change.newPassword,
  };
}

/** อ่าน `details.rule` จาก error envelope โดยไม่เชื่อรูปร่างของ body ที่ได้มา */
function ruleOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }
  const details = (payload as Record<string, unknown>).details;
  if (typeof details !== 'object' || details === null) {
    return null;
  }
  const rule = (details as Record<string, unknown>).rule;
  return typeof rule === 'string' && rule.trim() !== '' ? rule.trim() : null;
}

/**
 * เปลี่ยนรหัสผ่านของตัวเอง
 *
 * รหัสผ่านทั้งสองตัวอยู่ในพารามิเตอร์และใน body ที่ส่งออกเท่านั้น ไม่ถูกเก็บต่อ
 * ไม่ถูกคืนกลับ และไม่ปรากฏในสถานะความล้มเหลวใด ๆ ที่ฟังก์ชันนี้คืน
 */
export async function changePassword(
  change: PasswordChange,
  options: PasswordChangeOptions,
): Promise<PasswordChangeResult> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, CHANGE_PASSWORD_PATH);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // ประกอบเองแทนที่จะพึ่ง `authorizedFetch` — ดู doc ของโมดูลข้อ 1 และ 2
        Authorization: `Bearer ${options.token}`,
      },
      body: JSON.stringify(serializePasswordChangeBody(change)),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // ส่งต่อเฉพาะ `message` ของ error ไม่ใช่ทั้ง object — body ที่มีรหัสผ่านอยู่ต้อง
    // ไม่มีทางติดไปกับสถานะที่หน้าจอเอาไปเรนเดอร์
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (response.status === 204) {
    return { status: 'changed' };
  }

  if (response.status === 401) {
    return { status: 'rejected' };
  }

  if (response.status === 403) {
    return { status: 'forbidden' };
  }

  if (response.status === 400) {
    return { status: 'bad-request' };
  }

  if (response.status === 422) {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // body ที่อ่านไม่ออกไม่ควรกลบ status ที่อ่านออกแล้ว — ยังเป็น 422 อยู่ดี
      payload = null;
    }
    return { status: 'policy-violation', rule: ruleOf(payload) };
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  // `2xx` ที่ไม่ใช่ `204` ไม่อยู่ใน contract ของ endpoint นี้ ถือว่าเซิร์ฟเวอร์ตัวนั้น
  // ไม่ใช่ตัวที่ contract อธิบายไว้ — ดีกว่าเดาว่าเปลี่ยนรหัสสำเร็จแล้ว
  return { status: 'server-error', httpStatus: response.status };
}
